import {
  getMultiFactorResolver,
  multiFactor,
  sendEmailVerification,
  TotpMultiFactorGenerator,
} from 'firebase/auth';
import { trackSentryErrorByTag } from '../sentry/common';

export const TOTP_ISSUER = 'Amberscript';
export const TOTP_DISPLAY_NAME = 'Authenticator app';

export const MFA_REQUIRED_ERROR_CODE = 'auth/multi-factor-auth-required';
export const INVALID_CODE_ERROR_CODE = 'auth/invalid-verification-code';
export const TWO_FACTOR_CANCELLED_ERROR_CODE = 'auth/two-factor-cancelled';
export const TWO_FACTOR_UNAVAILABLE_ERROR_CODE = 'auth/two-factor-unavailable';
export const TWO_FACTOR_REMOVAL_NOT_ALLOWED_ERROR_CODE =
  'auth/two-factor-removal-not-allowed';
export const TWO_FACTOR_EMAIL_UNVERIFIED_ERROR_CODE =
  'auth/two-factor-email-unverified';

/**
 * Whether freelancer 2FA is switched on for this environment
 * @returns {boolean}
 */
export function isTwoFactorEnabled() {
  return process.env.REACT_APP_FF_FREELANCER_2FA !== undefined
    ? JSON.parse(process.env.REACT_APP_FF_FREELANCER_2FA)
    : false;
}

/**
 * Date from which enrolling in 2FA is mandatory. Before it, enrolment can be skipped.
 * @returns {Date|null} null when no enforcement date is configured
 */
export function getTwoFactorEnforcementDate() {
  const value = process.env.REACT_APP_FREELANCER_2FA_ENFORCEMENT_DATE;
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * @param {Date} now
 * @returns {boolean} true when users without 2FA must enrol before logging in
 */
export function isTwoFactorEnforced(now = new Date()) {
  const enforcementDate = getTwoFactorEnforcementDate();
  return enforcementDate !== null && now >= enforcementDate;
}

// Firebase custom claim with the admin's per user override, see backend#2325
const REQUIREMENT_CLAIM = 'twoFactorRequirement';
export const TWO_FACTOR_REQUIRED = 'REQUIRED';
export const TWO_FACTOR_EXEMPT = 'EXEMPT';

/**
 * Whether the signed in user must use 2FA: from the enforcement date on, unless an
 * admin required it earlier or exempted the user.
 * @param {object} user Firebase user
 * @param {boolean} [refresh] fetch a new ID token, with the requirement as it is now
 * @returns {Promise<boolean>}
 */
export async function isTwoFactorRequiredFor(user, refresh = false) {
  const { claims } = await user.getIdTokenResult(refresh);
  switch (claims[REQUIREMENT_CLAIM]) {
    case TWO_FACTOR_REQUIRED:
      return true;
    case TWO_FACTOR_EXEMPT:
      return false;
    default:
      return isTwoFactorEnforced();
  }
}

function createTwoFactorError(code, message) {
  const error = new Error(message);
  error.code = code;
  return error;
}

export function createTwoFactorCancelledError() {
  return createTwoFactorError(
    TWO_FACTOR_CANCELLED_ERROR_CODE,
    'Two-factor authentication was cancelled',
  );
}

/**
 * Asks the user for TOTP codes until the second factor is accepted
 * @param {function(string): Promise<void>} verify throws on a wrong code
 * @param {function({invalidCode: boolean}): Promise<string>} requestCode
 *   rejects when the user cancels
 * @param {boolean} invalidCode whether the previous code was wrong
 */
async function verifyWithRetries(verify, requestCode, invalidCode = false) {
  const code = await requestCode({ invalidCode });
  try {
    return await verify(code);
  } catch (error) {
    if (error.code !== INVALID_CODE_ERROR_CODE) {
      throw error;
    }
    return verifyWithRetries(verify, requestCode, true);
  }
}

/**
 * Completes a sign in that Firebase rejected because the user has 2FA enrolled
 * @param {object} auth Firebase auth instance
 * @param {object} error the auth/multi-factor-auth-required error
 * @param {function} requestCode see verifyWithRetries
 * @returns {Promise<object>} the signed in user
 */
export async function resolveTotpSignIn(auth, error, requestCode) {
  const resolver = getMultiFactorResolver(auth, error);
  const totpHint = resolver.hints.find(
    (hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID,
  );
  if (!totpHint) {
    throw createTwoFactorError(
      TWO_FACTOR_UNAVAILABLE_ERROR_CODE,
      'No authenticator app is enrolled for this account',
    );
  }

  const credential = await verifyWithRetries(
    (code) =>
      resolver.resolveSignIn(
        TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, code),
      ),
    requestCode,
  );
  return credential.user;
}

/**
 * @param {object} user Firebase user
 * @returns {boolean}
 */
export function hasTotpEnrolled(user) {
  return getTotpFactor(user) !== undefined;
}

function getTotpFactor(user) {
  return multiFactor(user).enrolledFactors.find(
    (factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID,
  );
}

// Must match the route of the VerifyEmail page
const VERIFY_EMAIL_PATH = '/verify-email';
const UNAUTHORIZED_CONTINUE_URL_ERROR_CODES = [
  'auth/unauthorized-continue-uri',
  'auth/invalid-continue-uri',
];

/**
 * Sends the link that verifies the user's email address. The link opens our own
 * verify-email page instead of Firebase's unstyled one.
 * @param {object} user Firebase user
 * @returns {Promise<boolean>} false when Firebase refused, e.g. too many requests
 */
export async function sendVerificationEmail(user) {
  try {
    try {
      await sendEmailVerification(user, {
        url: `${window.location.origin}${VERIFY_EMAIL_PATH}`,
        handleCodeInApp: true,
      });
    } catch (error) {
      // This domain is not authorised in Firebase: fall back to Firebase's page
      if (!UNAUTHORIZED_CONTINUE_URL_ERROR_CODES.includes(error.code)) {
        throw error;
      }
      trackSentryErrorByTag(error, 'freelancer-2fa-email-verification-domain');
      await sendEmailVerification(user);
    }
    return true;
  } catch (error) {
    trackSentryErrorByTag(error, 'freelancer-2fa-email-verification');
    return false;
  }
}

/**
 * Firebase only lets users with a verified email enrol a second factor.
 * Picks up a verification done since the sign in, otherwise rejects.
 * @param {object} user Firebase user
 */
async function ensureVerifiedEmail(user) {
  if (user.emailVerified) {
    return;
  }
  await user.reload();
  if (!user.emailVerified) {
    throw createTwoFactorError(
      TWO_FACTOR_EMAIL_UNVERIFIED_ERROR_CODE,
      'The email address must be verified before enrolling a second factor',
    );
  }
  // The ID token still says unverified until it is refreshed
  await user.getIdToken(true);
}

/**
 * Enrols an authenticator app for a freshly signed in user.
 * Rejects with TWO_FACTOR_EMAIL_UNVERIFIED_ERROR_CODE while the email is unverified.
 * @param {object} user Firebase user
 * @param {function(object): Promise<string|null>} requestEnrolmentCode
 *   receives {secretKey, qrCodeUrl, required, invalidCode}; resolves with the code,
 *   or null when the user skips enrolment (only allowed when not required)
 * @param {boolean} required whether skipping is allowed
 * @returns {Promise<boolean>} true when 2FA was enrolled, false when skipped
 */
export async function enrolTotp(user, requestEnrolmentCode, required) {
  await ensureVerifiedEmail(user);

  let secret;
  try {
    const session = await multiFactor(user).getSession();
    secret = await TotpMultiFactorGenerator.generateSecret(session);
  } catch (error) {
    // e.g. TOTP is not enabled on the Firebase project yet
    trackSentryErrorByTag(error, 'freelancer-2fa-generate-secret');
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('Firebase refused to start 2FA enrolment:', error);
    }
    throw createTwoFactorError(
      TWO_FACTOR_UNAVAILABLE_ERROR_CODE,
      error.message,
    );
  }

  const enrolmentDetails = {
    secretKey: secret.secretKey,
    qrCodeUrl: secret.generateQrCodeUrl(user.email, TOTP_ISSUER),
    required,
  };

  let skipped = false;
  await verifyWithRetries(
    async (code) => {
      if (code === null) {
        if (required) {
          throw createTwoFactorCancelledError();
        }
        skipped = true;
        return;
      }
      await multiFactor(user).enroll(
        TotpMultiFactorGenerator.assertionForEnrollment(secret, code),
        TOTP_DISPLAY_NAME,
      );
    },
    ({ invalidCode }) =>
      requestEnrolmentCode({ ...enrolmentDetails, invalidCode }),
  );
  return !skipped;
}

/**
 * Offers (or, after the enforcement date, requires) a user without 2FA to enrol an
 * authenticator app. Must run right after the first factor sign in.
 * @param {object} user Firebase user
 * @param {function(object): Promise<string|null>} requestEnrolmentCode see enrolTotp
 * @returns {Promise<boolean>} true when 2FA was enrolled
 */
export async function enrolTotpIfNeeded(user, requestEnrolmentCode) {
  if (!isTwoFactorEnabled() || hasTotpEnrolled(user)) {
    return false;
  }
  const required = await isTwoFactorRequiredFor(user);
  // During the grace period, don't send a verification link on every login;
  // the user gets one when setting up 2FA from the Security settings
  if (!required && !user.emailVerified) {
    return false;
  }
  try {
    return await enrolTotp(user, requestEnrolmentCode, required);
  } catch (error) {
    // During the grace period an unavailable 2FA setup must not block the login
    if (error.code === TWO_FACTOR_UNAVAILABLE_ERROR_CODE && !required) {
      return false;
    }
    if (error.code === TWO_FACTOR_EMAIL_UNVERIFIED_ERROR_CODE) {
      await sendVerificationEmail(user);
    }
    throw error;
  }
}

/**
 * Removes the authenticator app of a freshly signed in user.
 * Not allowed while 2FA is required for the user.
 * @param {object} user Firebase user
 */
export async function unenrolTotp(user) {
  // A fresh token: an admin may have required 2FA since the sign in
  if (await isTwoFactorRequiredFor(user, true)) {
    throw createTwoFactorError(
      TWO_FACTOR_REMOVAL_NOT_ALLOWED_ERROR_CODE,
      'Two-factor authentication is required and cannot be removed',
    );
  }
  const factor = getTotpFactor(user);
  if (factor) {
    await multiFactor(user).unenroll(factor);
  }
}
